import { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Form, Input, Checkbox, Typography, theme } from 'antd'
import { UserOutlined, LockOutlined, IdcardOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import { PartnerSurveyStepPromotion } from './PartnerSurveyStepPromotion'
import { PartnerSurveyStepAudience } from './PartnerSurveyStepAudience'
import { PartnerSurveyStepNiche } from './PartnerSurveyStepNiche'
import { PartnerSurveyStepExperience } from './PartnerSurveyStepExperience'
import { FullWidthButton } from './styles/SignUpFormStyles'
import {
  FlowContainer,
  ProgressContainer,
  ProgressBar,
  ProgressFill,
  StepIndicator,
  StepContent,
  NavigationButtons,
  BackButton,
  ContinueButton,
} from './styles/SignUpFlowStyles'
import { partnerTermsContent } from '../../content/partnerTermsContent'
import { SPACING, BORDER_RADIUS, TYPOGRAPHY, FONT_WEIGHT } from '../../styles/theme'

const { Text } = Typography
const { useToken } = theme

const TOTAL_STEPS = 5

const TermsScrollBox = styled.div<{ $bgColor: string; $borderColor: string }>`
  max-height: 200px;
  overflow-y: auto;
  padding: ${SPACING.md};
  background: ${(props) => props.$bgColor};
  border: 1px solid ${(props) => props.$borderColor};
  border-radius: ${BORDER_RADIUS.md};
  margin-bottom: ${SPACING.md};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  line-height: 1.6;
`

const TermsSection = styled.div`
  margin-bottom: ${SPACING.md};

  &:last-child {
    margin-bottom: 0;
  }
`

const TermsSectionTitle = styled.h4`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  margin: 0 0 ${SPACING.xs} 0;
  color: var(--color-text-primary);
`

const TermsParagraph = styled.p`
  margin: 0 0 ${SPACING.xs} 0;
  color: var(--color-text-secondary);

  &:last-child {
    margin-bottom: 0;
  }
`

export interface PartnerSignUpFlowData {
  email: string
  password: string
  fullName: string
  partnerTermsAccepted: boolean
  newsletterOptIn: boolean
  surveyPromotionChannels: string[]
  surveyPromotionChannelsOther: string
  surveyAudienceSize: string
  surveyNiche: string
  surveyNicheOther: string
  surveyExperience: string
}

interface PartnerSignUpFlowProps {
  onFinish: (values: PartnerSignUpFlowData) => void
  loading: boolean
}

export function PartnerSignUpFlow({ onFinish, loading }: PartnerSignUpFlowProps) {
  const [form] = Form.useForm()
  const { token } = useToken()
  const [currentStep, setCurrentStep] = useState(1)

  // Account info state (from step 1 form)
  const [accountInfo, setAccountInfo] = useState<{
    email: string
    password: string
    fullName: string
    partnerTermsAccepted: boolean
    newsletterOptIn: boolean
  } | null>(null)

  // Survey state
  const [surveyPromotionChannels, setSurveyPromotionChannels] = useState<string[]>([])
  const [surveyPromotionChannelsOther, setSurveyPromotionChannelsOther] = useState('')
  const [surveyAudienceSize, setSurveyAudienceSize] = useState<string | null>(null)
  const [surveyNiche, setSurveyNiche] = useState<string | null>(null)
  const [surveyNicheOther, setSurveyNicheOther] = useState('')
  const [surveyExperience, setSurveyExperience] = useState<string | null>(null)

  const handleAccountFormFinish = (values: {
    email: string
    password: string
    fullName: string
    partnerTermsAccepted: boolean
    newsletterOptIn: boolean
  }) => {
    setAccountInfo(values)
    setCurrentStep(2)
  }

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(1, prev - 1))
  }

  const handleContinue = () => {
    if (currentStep === TOTAL_STEPS) {
      // Final step - submit everything
      if (accountInfo) {
        onFinish({
          ...accountInfo,
          surveyPromotionChannels,
          surveyPromotionChannelsOther,
          surveyAudienceSize: surveyAudienceSize ?? '',
          surveyNiche: surveyNiche ?? '',
          surveyNicheOther,
          surveyExperience: surveyExperience ?? '',
        })
      }
    } else {
      setCurrentStep((prev) => prev + 1)
    }
  }

  const canContinue = () => {
    switch (currentStep) {
      case 2:
        return surveyPromotionChannels.length > 0
      case 3:
        return surveyAudienceSize !== null
      case 4:
        return surveyNiche !== null
      case 5:
        return surveyExperience !== null
      default:
        return true
    }
  }

  const progress = (currentStep / TOTAL_STEPS) * 100

  // Step 1: Account info + Partner Terms
  if (currentStep === 1) {
    return (
      <FlowContainer>
        <ProgressContainer>
          <ProgressBar>
            <ProgressFill $progress={progress} />
          </ProgressBar>
          <StepIndicator>
            Step {currentStep} of {TOTAL_STEPS}
          </StepIndicator>
        </ProgressContainer>

        <Form
          form={form}
          name="partner-signup"
          onFinish={handleAccountFormFinish}
          layout="vertical"
          requiredMark={false}
          size="large"
          initialValues={accountInfo ?? {}}
        >
          <Form.Item
            name="email"
            rules={[
              { required: true, message: 'Please input your email!' },
              { type: 'email', message: 'Please enter a valid email!' },
            ]}
          >
            <Input prefix={<UserOutlined />} placeholder="Email" autoComplete="email" />
          </Form.Item>

          <Form.Item
            name="fullName"
            label="Full Name"
            rules={[
              { required: true, message: 'Please input your full name!' },
              { min: 2, message: 'Full name must be at least 2 characters!' },
            ]}
          >
            <Input prefix={<IdcardOutlined />} placeholder="Full Name" autoComplete="name" />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: 'Please input your password!' },
              { min: 6, message: 'Password must be at least 6 characters!' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve()
                  if (!/[A-Z]/.test(value))
                    return Promise.reject(
                      new Error('Password must contain at least one uppercase letter')
                    )
                  if (!/[a-z]/.test(value))
                    return Promise.reject(
                      new Error('Password must contain at least one lowercase letter')
                    )
                  if (!/[0-9]/.test(value))
                    return Promise.reject(new Error('Password must contain at least one number'))
                  return Promise.resolve()
                },
              },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Password"
              autoComplete="new-password"
            />
          </Form.Item>

          <Text strong style={{ display: 'block', marginBottom: SPACING.sm }}>
            Partner Program Terms
          </Text>
          <TermsScrollBox $bgColor={token.colorFillTertiary} $borderColor={token.colorBorder}>
            {partnerTermsContent.sections.map((section, index) => (
              <TermsSection key={index}>
                <TermsSectionTitle>{section.title}</TermsSectionTitle>
                {section.content.map((paragraph, pIndex) => (
                  <TermsParagraph key={pIndex}>{paragraph}</TermsParagraph>
                ))}
              </TermsSection>
            ))}
          </TermsScrollBox>

          <Form.Item
            name="partnerTermsAccepted"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error('You must agree to the Partner Program Terms to continue')
                      ),
              },
            ]}
          >
            <Checkbox>
              <Text>
                I agree to the{' '}
                <RouterLink to="/partner-terms" target="_blank">
                  Partner Program Terms
                </RouterLink>
                ,{' '}
                <RouterLink to="/terms" target="_blank">
                  Terms of Service
                </RouterLink>
                , and{' '}
                <RouterLink to="/privacy" target="_blank">
                  Privacy Policy
                </RouterLink>
              </Text>
            </Checkbox>
          </Form.Item>

          <Form.Item name="newsletterOptIn" valuePropName="checked">
            <Checkbox>
              <Text>Subscribe to partner updates and tips</Text>
            </Checkbox>
          </Form.Item>

          <Form.Item>
            <FullWidthButton type="primary" htmlType="submit">
              Continue
            </FullWidthButton>
          </Form.Item>
        </Form>
      </FlowContainer>
    )
  }

  // Steps 2-5: Survey questions
  return (
    <FlowContainer>
      <ProgressContainer>
        <ProgressBar>
          <ProgressFill $progress={progress} />
        </ProgressBar>
        <StepIndicator>
          Step {currentStep} of {TOTAL_STEPS}
        </StepIndicator>
      </ProgressContainer>

      <StepContent key={currentStep}>
        {currentStep === 2 && (
          <PartnerSurveyStepPromotion
            values={surveyPromotionChannels}
            otherValue={surveyPromotionChannelsOther}
            onChange={setSurveyPromotionChannels}
            onOtherChange={setSurveyPromotionChannelsOther}
          />
        )}

        {currentStep === 3 && (
          <PartnerSurveyStepAudience value={surveyAudienceSize} onChange={setSurveyAudienceSize} />
        )}

        {currentStep === 4 && (
          <PartnerSurveyStepNiche
            value={surveyNiche}
            otherValue={surveyNicheOther}
            onChange={setSurveyNiche}
            onOtherChange={setSurveyNicheOther}
          />
        )}

        {currentStep === 5 && (
          <PartnerSurveyStepExperience value={surveyExperience} onChange={setSurveyExperience} />
        )}
      </StepContent>

      <NavigationButtons>
        <BackButton onClick={handleBack}>Back</BackButton>
        <ContinueButton
          onClick={handleContinue}
          disabled={!canContinue() || loading}
          $disabled={!canContinue() || loading}
        >
          {loading
            ? 'Submitting...'
            : currentStep === TOTAL_STEPS
              ? 'Submit Application'
              : 'Continue'}
        </ContinueButton>
      </NavigationButtons>
    </FlowContainer>
  )
}
